import React, { useState, useEffect, type ReactNode } from 'react';
import {
  FileText,
  Box,
  MapPin,
  Zap,
  Calendar,
  Plus,
  Loader2,
  Upload,
  Download,
  Trash2,
  Building2,
  Cable,
  ListTree,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { InputNumber } from 'primereact/inputnumber';
import RightDrawer from '../../../shared/components/form/RightDrawer';
import Select from '../../../shared/components/form/Select';
import Button from '../../../shared/components/ui/button/Button';
import { equipmentService } from '../../../services/equipmentService';
import makeAndModelService from '../../../services/makeAndModelService';
import { fileService, looksLikeImageMediaUrl } from '../../../services/fileService';
import { useResolvedMediaUrl } from '../../../hooks/useResolvedMediaUrl';
import { ResolvedImage } from '../../../components/media/ResolvedImage';
import { isStringAsset, isCombinerBoxAssetType } from './project-asset-tree-view/assetTypeConfig';
import { getStringEquipmentModuleCount } from './project-asset-tree-view/solarPlantSizing';

/** DC string row (not combiner): simplified drawer — module count only; no full equipment tabs */
function isStringEquipmentSummaryRow(data: any): boolean {
  const t = String(data?.assetType || data?.hierarchyLevel || '');
  return isStringAsset(t) && !isCombinerBoxAssetType(t);
}

// Design tokens – align with app forms (ManufacturerForm, CheckpointTable)
const INPUT_BASE =
  'h-11 w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-[#339274] focus:ring-[#339274]/20 transition-all duration-200';
const LABEL_CLASS = 'block text-sm font-semibold text-gray-700 mb-1.5';
const SECTION_TITLE = 'text-base font-semibold text-gray-900 flex items-center gap-2';
const CARD_SECTION = 'bg-gray-50 rounded-xl p-5 space-y-4';
const TAB_PANEL = 'pt-4 pb-2';

const TABS = [
  { id: 0, label: 'General', icon: Box },
  { id: 1, label: 'Location', icon: MapPin },
  { id: 2, label: 'Specs', icon: Zap },
  { id: 3, label: 'Lifecycle', icon: Calendar },
  { id: 4, label: 'Documents', icon: FileText },
] as const;

function OpenResolvedDocButton({
  documentFile,
  className,
  title,
  children,
}: {
  documentFile: string;
  className?: string;
  title?: string;
  children: ReactNode;
}) {
  const resolved = useResolvedMediaUrl(documentFile);
  return (
    <button
      type="button"
      disabled={!resolved}
      onClick={() => resolved && window.open(resolved, '_blank', 'noopener,noreferrer')}
      className={className}
      title={title}
    >
      {children}
    </button>
  );
}

interface EquipmentEditDrawerProps {
    open: boolean;
    onClose: () => void;
    equipment: any | null;
    manufacturers: Array<{ _id?: string; id?: string; name: string }>;
    makeAndModels: Array<{ _id?: string; id?: string; name: string; manufacturerId?: string }>;
    onSuccess: () => void;
    /** When provided, shows "Add child equipment" in details and calls this with current equipment as parent */
    onAddChild?: (equipment: any) => void;
    /** When provided, shows + DC Cable / + AC Cable next to Add child and opens bulk-add for that cable type under this equipment */
    onAddCableChild?: (equipment: any, kind: 'dc' | 'ac') => void;
    /** When provided, shows "Remove equipment" — parent should run confirmation + delete (e.g. same as tree delete) */
    onDeleteEquipment?: () => void;
}

interface FormData {
    // Identification
    equipmentName: string;
    serialNumber: string;
    assetType: string;
    criticality: 'Low' | 'Medium' | 'High' | 'Critical';

    // Make & Model
    manufacturerId: string;
    manufacturer: string;
    makeAndModelId: string;
    makeAndModel: string;

    // Location
    zone: string;
    block: string;
    subLocation: string;
    installationLocation: {
        area: string;
        rack: string;
        position: string;
    };

    // Technical
    technicalSpecifications: string;
    electricalRatings: {
        voltage: string;
        current: string;
        power: string;
        frequency: string;
        phase: string;
        class: string;
    };

    // Lifecycle
    installationDate: string;
    commissioningDate: string;
    installedQuantity: number;

    // Vendor & Support
    vendor: {
        name: string;
        contactPerson: string;
        phone: string;
        email: string;
        address: string;
    };
    warranty: {
        startDate: string;
        endDate: string;
        provider: string;
        terms: string;
        termsYears: number;
    };
    insurance: {
        policyNumber: string;
        provider: string;
        startDate: string;
        endDate: string;
        coverageDetails: string;
    };

    notes: string;
}



const CRITICALITY_OPTIONS: { label: string; value: string }[] = ['Low', 'Medium', 'High', 'Critical'].map((c) => ({ label: c, value: c }));

const DOCUMENT_TYPES = [
    'GA Drawings / GTP & Datasheets',
    'Test Reports',
    'Raw Material Test Certificates (RMTC)',
    'Warranty Certificates',
    'Manual',
    'Specification',
    'Certificate',
    'Warranty',
    'Insurance',
    'Insurance Certificate',
    'Installation',
    'Maintenance',
    'Other'
];

const CRITICALITY_STYLES: Record<string, string> = {
  Low: 'bg-slate-100 text-slate-700 border-slate-200',
  Medium: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  High: 'bg-amber-50 text-amber-700 border-amber-200',
  Critical: 'bg-red-50 text-red-700 border-red-200',
};

const EquipmentEditDrawer: React.FC<EquipmentEditDrawerProps> = ({
    open,
    onClose,
    equipment,
    manufacturers,
    makeAndModels: initialMakeAndModels,
    onSuccess,
    onAddChild,
    onAddCableChild,
    onDeleteEquipment,
}) => {
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [saving, setSaving] = useState(false);
    const [availableModels, setAvailableModels] = useState<Array<{ id: string; name: string; _id?: string }>>([]);
    const [isLoadingModels, setIsLoadingModels] = useState(false);
    const [emailError, setEmailError] = useState('');

    // Initial Form State
    const [form, setForm] = useState<FormData>({
        equipmentName: '',
        serialNumber: '',
        assetType: '',
        criticality: 'Medium',
        manufacturerId: '',
        manufacturer: '',
        makeAndModelId: '',
        makeAndModel: '',
        zone: '',
        block: '',
        subLocation: '',
        installationLocation: { area: '', rack: '', position: '' },
        technicalSpecifications: '',
        electricalRatings: { voltage: '', current: '', power: '', frequency: '', phase: '', class: '' },
        installationDate: '',
        commissioningDate: '',
        installedQuantity: 1,
        vendor: { name: '', contactPerson: '', phone: '', email: '', address: '' },
        warranty: { startDate: '', endDate: '', provider: '', terms: '', termsYears: 0 },
        insurance: { policyNumber: '', provider: '', startDate: '', endDate: '', coverageDetails: '' },
        notes: ''
    });

    const [documents, setDocuments] = useState<any[]>([]);
    const [uploadingDoc, setUploadingDoc] = useState(false);
    const [newDocument, setNewDocument] = useState({
        documentName: '',
        documentType: 'Manual',
        documentFile: ''
    });
    const [uploadingWarrantyDoc, setUploadingWarrantyDoc] = useState(false);
    const [uploadingInsuranceDoc, setUploadingInsuranceDoc] = useState(false);

    /** When set, drawer shows string-only summary (PV slot positions) instead of full tabs */
    const [stringModuleCount, setStringModuleCount] = useState<number | null>(null);
    const [stringMeta, setStringMeta] = useState<{
      equipmentCode: string;
      recordId: string;
      assetLabel: string;
    } | null>(null);

    useEffect(() => {
        if (!open) {
            setStringModuleCount(null);
            setStringMeta(null);
        }
    }, [open]);

    // Initialize Form Data
    useEffect(() => {
        const loadData = async () => {
            if (!equipment) return;

            let data = equipment;
            try {
                // Always fetch fresh data when drawer opens
                const freshData = await equipmentService.getById(equipment._id || equipment.id);
                data = freshData || equipment;
            } catch (err) {
                console.warn("Could not fetch fresh equipment details, using prop data", err);
                // Fall back to prop data if fetch fails
                data = equipment;
            }

            if (isStringEquipmentSummaryRow(data)) {
                setStringModuleCount(getStringEquipmentModuleCount(data));
                setStringMeta({
                    equipmentCode: String(data.equipmentId || ''),
                    recordId: String(data._id || data.id || ''),
                    assetLabel: String(data.assetType || data.hierarchyLevel || 'String'),
                });
                setForm((prev) => ({
                    ...prev,
                    equipmentName: data.equipmentName || data.name || '',
                    assetType: data.assetType || data.hierarchyLevel || '',
                }));
                setDocuments([]);
                return;
            }

            setStringModuleCount(null);
            setStringMeta(null);

            // Safe Date Parser
            const formatDate = (dateString?: string) => {
                if (!dateString) return '';
                try {
                    const date = new Date(dateString);
                    if (isNaN(date.getTime())) return '';
                    return date.toISOString().split('T')[0];
                } catch (e) {
                    return '';
                }
            };

            // Improved Manufacturer Resolution
            let manId = '';
            let manName = '';

            if (data.manufacturerId) {
                if (typeof data.manufacturerId === 'object') {
                    manId = data.manufacturerId._id || data.manufacturerId.id || '';
                    manName = data.manufacturerId.name || '';
                } else {
                    manId = data.manufacturerId;
                }
            }

            if (!manName && data.manufacturer) {
                manName = data.manufacturer;
            }

            const matchById = manufacturers.find(m => (m._id === manId || m.id === manId));
            if (matchById) {
                manName = matchById.name;
                manId = matchById._id || matchById.id || '';
            } else {
                const nameToSearch = manName || manId;
                const matchByName = manufacturers.find(m => m.name === nameToSearch);
                if (matchByName) {
                    manId = matchByName._id || matchByName.id || '';
                    manName = matchByName.name;
                }
            }

            // Improved Model Resolution
            let modelId = '';
            let modelName = '';

            if (data.makeAndModelId) {
                if (typeof data.makeAndModelId === 'object') {
                    modelId = data.makeAndModelId._id || data.makeAndModelId.id || '';
                    modelName = data.makeAndModelId.name || '';
                } else {
                    modelId = data.makeAndModelId;
                }
            }
            if (!modelName && data.makeAndModel) {
                modelName = data.makeAndModel;
            }

            setForm({
                equipmentName: data.equipmentName || data.name || '',
                serialNumber: data.serialNumber || '',
                assetType: data.assetType || data.hierarchyLevel || '',
                criticality: data.criticality || 'Medium',
                manufacturerId: manId,
                manufacturer: manName,
                makeAndModelId: modelId,
                makeAndModel: modelName,
                zone: data.zone || '',
                block: data.block || '',
                subLocation: data.subLocation || '',
                installationLocation: {
                    area: data.installationLocation?.area || '',
                    rack: data.installationLocation?.rack || '',
                    position: data.installationLocation?.position || '',
                },
                technicalSpecifications: data.technicalSpecifications || '',
                electricalRatings: {
                    voltage: data.electricalRatings?.voltage || '',
                    current: data.electricalRatings?.current || '',
                    power: data.electricalRatings?.power || '',
                    frequency: data.electricalRatings?.frequency || '',
                    phase: data.electricalRatings?.phase || '',
                    class: data.electricalRatings?.class || '',
                },
                installationDate: formatDate(data.installationDate),
                commissioningDate: formatDate(data.commissioningDate),
                installedQuantity: data.installedQuantity || 1,
                vendor: {
                    name: data.vendor?.name || '',
                    contactPerson: data.vendor?.contactPerson || '',
                    phone: data.vendor?.phone || '',
                    email: data.vendor?.email || '',
                    address: data.vendor?.address || '',
                },
                warranty: {
                    startDate: formatDate(data.warranty?.startDate),
                    endDate: formatDate(data.warranty?.endDate),
                    provider: data.warranty?.provider || '',
                    terms: data.warranty?.terms || '',
                    termsYears: data.warranty?.termsYears || 0,
                },
                insurance: {
                    policyNumber: data.insurance?.policyNumber || '',
                    provider: data.insurance?.provider || '',
                    startDate: formatDate(data.insurance?.startDate),
                    endDate: formatDate(data.insurance?.endDate),
                    coverageDetails: data.insurance?.coverageDetails || '',
                },
                notes: data.notes || '',
            });

            setDocuments(data.attachedDocuments || []);
        };

        if (open && equipment) {
            loadData();
            setActiveTabIndex(0);
        }
    }, [open, equipment, manufacturers]);

    // Fetch Models on Manufacturer Change
    useEffect(() => {
        const fetchModels = async () => {
            let idToUse = form.manufacturerId;

            if (!idToUse) {
                setAvailableModels([]);
                return;
            }

            const idString = String(idToUse);

            if (!/^[0-9a-fA-F]{24}$/.test(idString)) {
                const found = manufacturers.find(m => m.name === idString);
                const foundId = found?._id || found?.id;
                if (foundId && /^[0-9a-fA-F]{24}$/.test(String(foundId))) {
                    idToUse = foundId;
                } else {
                    setAvailableModels([]);
                    return;
                }
            }

            const finalId = String(idToUse);
            if (!/^[0-9a-fA-F]{24}$/.test(finalId)) {
                setAvailableModels([]);
                return;
            }

            setIsLoadingModels(true);
            try {
                const models = await makeAndModelService.getMakeAndModelsForDropdown(finalId);
                const mappedModels = models.map((m: any) => ({
                    id: m.id || m._id,
                    _id: m.id || m._id,
                    name: m.name
                }));
                setAvailableModels(mappedModels);
            } catch (error) {
                console.error("Failed to fetch models", error);
                setAvailableModels([]);
            } finally {
                setIsLoadingModels(false);
            }
        };

        fetchModels();
    }, [form.manufacturerId, manufacturers]);


    const handleChange = (field: keyof FormData, value: any) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleNestedChange = (section: keyof FormData, field: string, value: any) => {
        setForm(prev => ({
            ...prev,
            [section]: {
                ...(prev[section] as object),
                [field]: value
            }
        }));
    };

    const handleManufacturerChange = (value: string | undefined) => {
        const id = value ?? '';
        const manu = manufacturers.find(m => (m._id === id || m.id === id));
        setForm(prev => ({
            ...prev,
            manufacturerId: id,
            manufacturer: manu?.name || '',
            makeAndModelId: '',
            makeAndModel: ''
        }));
    };

    const handleModelChange = (value: string | undefined) => {
        const id = value ?? '';
        const model = availableModels.find(m => m.id === id || m._id === id);
        setForm(prev => ({
            ...prev,
            makeAndModelId: id,
            makeAndModel: model?.name || ''
        }));
    };

    // ── Input Filters ──────────────────────────────────────────
    /** Phone: only digits, +, -, spaces, parentheses allowed */
    const handlePhoneInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const filtered = e.target.value.replace(/[^0-9+\-()\s]/g, '');
        handleNestedChange('vendor', 'phone', filtered);
    };
    const handlePhoneKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Allow control keys (backspace, delete, arrows, tab, etc.)
        const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Home', 'End'];
        if (allowedKeys.includes(e.key)) return;
        // Allow Ctrl/Cmd combos (copy, paste, select all)
        if (e.ctrlKey || e.metaKey) return;
        // Allow only digits 0-9, +, -, (, ), space
        if (!/^[0-9+\-()\s]$/.test(e.key)) {
            e.preventDefault();
        }
    };

    /** Email: validate on change and show error */
    const handleEmailInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        handleNestedChange('vendor', 'email', val);
        if (val && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
            setEmailError('Please enter a valid email address');
        } else {
            setEmailError('');
        }
    };

    /** Contact Person: no digits allowed */
    const handleContactPersonInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const filtered = e.target.value.replace(/[0-9]/g, '');
        handleNestedChange('vendor', 'contactPerson', filtered);
    };
    const handleContactPersonKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Home', 'End'];
        if (allowedKeys.includes(e.key)) return;
        if (e.ctrlKey || e.metaKey) return;
        if (/^[0-9]$/.test(e.key)) {
            e.preventDefault();
        }
    };

    const handleSave = async () => {
        if (!equipment) return;
        setSaving(true);
        try {
            const equipmentId = equipment._id || equipment.id;

            // All fields except equipment name are optional; send only defined values for update
            const payload = {
                serialNumber: form.serialNumber || undefined,
                manufacturerId: form.manufacturerId || undefined,
                manufacturer: form.manufacturer || undefined,
                makeAndModelId: form.makeAndModelId || undefined,
                makeAndModel: form.makeAndModel || undefined,
                criticality: form.criticality || undefined,
                zone: form.zone || undefined,
                block: form.block || undefined,
                subLocation: form.subLocation || undefined,
                installationLocation: form.installationLocation,
                technicalSpecifications: form.technicalSpecifications || undefined,
                electricalRatings: form.electricalRatings,
                installationDate: form.installationDate || undefined,
                commissioningDate: form.commissioningDate || undefined,
                installedQuantity: form.installedQuantity != null ? Number(form.installedQuantity) : undefined,
                vendor: form.vendor,
                warranty: form.warranty,
                insurance: form.insurance,
                notes: form.notes || undefined
            };

            await equipmentService.updateEquipment(equipmentId, payload as any);
            toast.success('Equipment updated successfully');
            onSuccess();
            onClose();
        } catch (error: any) {
            console.error('Update Error', error);
            toast.error(error.message || 'Failed to update equipment');
        } finally {
            setSaving(false);
        }
    };

    const handleUploadDocument = async () => {
        if (!equipment || !newDocument.documentName || !newDocument.documentFile) {
            toast.error('Please fill in document name and file key or URL');
            return;
        }
        setUploadingDoc(true);
        try {
            await equipmentService.uploadEquipmentDocuments(equipment._id || equipment.id, [newDocument]);
            toast.success('Document uploaded successfully');
            setNewDocument({ documentName: '', documentType: 'Manual', documentFile: '' });

            const fresh = await equipmentService.getById(equipment._id || equipment.id);
            setDocuments(fresh.attachedDocuments || []);
        } catch (error: any) {
            toast.error(error.message || 'Failed to upload document');
        } finally {
            setUploadingDoc(false);
        }
    };

    const handleDeleteDocument = async (documentId: string) => {
        if (!equipment) return;
        if (!window.confirm('Are you sure you want to delete this document?')) return;
        try {
            await equipmentService.deleteEquipmentDocument(equipment._id || equipment.id, documentId);
            toast.success('Document deleted successfully');
            setDocuments(prev => prev.filter(d => d._id !== documentId));
        } catch (error: any) {
            toast.error(error.message || 'Failed to delete document');
        }
    };

    // ── S3 Upload Helper ───────────────────────────────────────
    // Uploads the file directly to S3 via a presigned PUT URL and returns
    // the canonical (unsigned) S3 object URL suitable to persist.
    const uploadFileToS3 = async (file: File): Promise<string> => {
        const uploaded = await fileService.uploadFile(file);
        return uploaded.url;
    };

    /** Inline upload for Warranty / Insurance docs */
    const handleSectionDocUpload = async (docType: 'Warranty' | 'Insurance') => {
        if (!equipment) return;
        const isWarranty = docType === 'Warranty';
        const setUploading = isWarranty ? setUploadingWarrantyDoc : setUploadingInsuranceDoc;

        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.txt';
        input.multiple = true;
        input.onchange = async (e: Event) => {
            const files = Array.from((e.target as HTMLInputElement).files || []);
            if (!files.length) return;

            setUploading(true);
            try {
                for (const file of files) {
                    if (file.size > 10 * 1024 * 1024) {
                        toast.error(`${file.name} exceeds 10 MB`);
                        continue;
                    }
                    try {
                        const fileUrl = await uploadFileToS3(file);
                        const baseName = file.name.replace(/\.[^/.]+$/, '');
                        await equipmentService.uploadEquipmentDocuments(
                            equipment._id || equipment.id,
                            [{ documentName: baseName, documentType: docType, documentFile: fileUrl }]
                        );
                        toast.success(`${baseName} uploaded`);
                    } catch (err: any) {
                        toast.error(`Failed to upload ${file.name}`);
                    }
                }
                // Refresh docs from server
                const fresh = await equipmentService.getById(equipment._id || equipment.id);
                setDocuments(fresh.attachedDocuments || []);
            } finally {
                setUploading(false);
            }
        };
        input.click();
    };

    const manufacturerOptions = manufacturers.map(m => ({ label: m.name, value: String(m._id || m.id || '') }));
    const modelOptions = availableModels.map(m => ({ label: m.name, value: String(m.id || m._id || '') }));
    const documentTypeOptions = DOCUMENT_TYPES.map(t => ({ label: t, value: t }));

    if (!open) return null;

    return (
        <RightDrawer
            open={open}
            onClose={onClose}
            title={stringModuleCount !== null ? 'String (modules)' : 'Equipment Details'}
            size="2xl"
            footer={equipment ? (
                <div className="flex flex-wrap gap-3 items-center justify-between w-full px-6 py-4 border-t border-gray-200 bg-gray-50/80">
                    <div className="flex gap-2 flex-wrap items-center">
                        {onAddChild && (
                            <Button variant="success" size="sm" startIcon={<Plus size={16} />} onClick={() => { if (equipment) { onAddChild(equipment); onClose(); } }}>
                                Add child equipment
                            </Button>
                        )}
                        {onAddCableChild && (
                            <>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="border-slate-200 text-slate-800 hover:bg-slate-50"
                                    startIcon={<Cable size={14} className="opacity-80" />}
                                    onClick={() => {
                                        if (equipment) {
                                            onAddCableChild(equipment, 'dc');
                                            onClose();
                                        }
                                    }}
                                >
                                    DC Cable
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="border-pink-200 bg-pink-50/80 text-pink-900 hover:bg-pink-100"
                                    startIcon={<Cable size={14} className="opacity-80" />}
                                    onClick={() => {
                                        if (equipment) {
                                            onAddCableChild(equipment, 'ac');
                                            onClose();
                                        }
                                    }}
                                >
                                    AC Cable
                                </Button>
                            </>
                        )}
                        {onDeleteEquipment && (
                            <Button
                                type="button"
                                variant="danger"
                                size="sm"
                                startIcon={<Trash2 size={16} />}
                                onClick={() => onDeleteEquipment()}
                            >
                                Remove equipment
                            </Button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
                        {stringModuleCount === null && (
                            <Button variant="primary" size="sm" disabled={saving} onClick={handleSave} startIcon={saving ? <Loader2 size={16} className="animate-spin" /> : undefined}>
                                {saving ? 'Saving…' : 'Update'}
                            </Button>
                        )}
                    </div>
                </div>
            ) : undefined}
        >
            <div className="pb-6">
                {!equipment ? (
                    <div className="flex items-center justify-center py-16">
                        <Loader2 className="w-10 h-10 animate-spin text-[#339274]" />
                        <span className="ml-3 text-gray-600">Loading equipment details…</span>
                    </div>
                ) : stringModuleCount !== null && stringMeta ? (
                    <>
                        <div className="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50/80 to-slate-50/80 p-4 mb-4 shadow-sm">
                            <div className="flex items-start gap-3">
                                <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-violet-100 border border-violet-200 shrink-0">
                                    <ListTree className="w-5 h-5 text-violet-700" />
                                </div>
                                <div className="min-w-0 flex-1 space-y-1">
                                    <h3 className="text-base font-semibold text-gray-900 truncate" title={form.equipmentName}>{form.equipmentName || '—'}</h3>
                                    <p className="text-xs text-gray-600">
                                        <span className="font-medium text-gray-700">Asset:</span> {stringMeta.assetLabel}
                                    </p>
                                    {stringMeta.equipmentCode && (
                                        <p className="text-xs font-mono text-slate-600 truncate" title={stringMeta.equipmentCode}>
                                            Code: {stringMeta.equipmentCode}
                                        </p>
                                    )}
                                    <p className="text-[11px] font-mono text-slate-500 truncate" title={stringMeta.recordId}>
                                        Id: {stringMeta.recordId}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <p className="text-xs text-gray-500 mb-3">
                            Checkpoints are managed at the <strong>project asset</strong> level, not per string. Use the sidebar asset card to open checkpoints.
                        </p>
                        <div className={CARD_SECTION}>
                            <h4 className={SECTION_TITLE}>
                                <ListTree size={18} />
                                Modules on this string
                            </h4>
                            <p className="text-xs text-gray-500 mb-3">
                                PV array is modeled as a count only (no per-module checkpoints). Full equipment editing for other types uses the standard details form.
                            </p>
                            <p className="text-sm text-gray-800 py-4 px-4 rounded-lg border border-gray-200 bg-white text-center font-medium">
                                {stringModuleCount === 0
                                    ? 'No modules recorded on this string'
                                    : stringModuleCount === 1
                                      ? '1 module'
                                      : `${stringModuleCount} modules`}
                            </p>
                        </div>
                    </>
                ) : (
                    <>
                <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-gray-50 to-slate-50/80 p-4 mb-5 shadow-sm">
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#339274]/10 border border-[#339274]/20 shrink-0">
                            <Box className="w-5 h-5 text-[#339274]" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <h3 className="text-base font-semibold text-gray-900 truncate" title={form.equipmentName}>{form.equipmentName || '—'}</h3>
                            <p className="text-sm text-gray-500 mt-0.5">S/N: {form.serialNumber || '—'}</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium border ${CRITICALITY_STYLES[form.criticality] ?? CRITICALITY_STYLES.Medium}`}>{form.criticality}</span>
                        </div>
                    </div>
                </div>
                <div className="flex gap-1 p-1 rounded-xl bg-gray-100 border border-gray-200 mb-4 overflow-x-auto">
                    {TABS.map(({ id, label, icon: Icon }) => (
                        <button key={id} type="button" onClick={() => setActiveTabIndex(id)} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 ${activeTabIndex === id ? 'bg-white text-[#339274] shadow-sm border border-gray-200' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`}>
                            <Icon size={16} />{label}
                        </button>
                    ))}
                </div>
                <AnimatePresence mode="wait">
                    {activeTabIndex === 0 && (
                        <motion.div key="general" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }} className={TAB_PANEL}>
                            <div className="space-y-5">
                                <div className={CARD_SECTION}>
                                    <h4 className={SECTION_TITLE}><Box size={18} /> Identification</h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="sm:col-span-2">
                                            <label className={LABEL_CLASS}>Equipment Name</label>
                                            <input type="text" value={form.equipmentName} readOnly className={`${INPUT_BASE} bg-gray-100 cursor-not-allowed text-gray-600`} title="Name cannot be edited" />
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASS}>Serial No. <span className="text-gray-400 font-normal">(optional)</span></label>
                                            <input type="text" value={form.serialNumber} onChange={(e) => handleChange('serialNumber', e.target.value)} placeholder="Serial number" className={INPUT_BASE} />
                                        </div>
                                    </div>
                                </div>
                                <div className={CARD_SECTION}>
                                    <h4 className={SECTION_TITLE}><Building2 size={18} /> Model</h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        <div><label className={LABEL_CLASS}>Manufacturer <span className="text-gray-400 font-normal">(optional)</span></label><Select options={manufacturerOptions} placeholder="Select manufacturer" value={form.manufacturerId || ''} onChange={(opt) => handleManufacturerChange(opt?.value ?? '')} menuPortalTarget={typeof document !== 'undefined' ? document.body : null} /></div>
                                        <div><label className={LABEL_CLASS}>Model <span className="text-gray-400 font-normal">(optional)</span> {isLoadingModels && '(…)'}</label><Select options={modelOptions} placeholder="Select model" value={form.makeAndModelId || ''} onChange={(opt) => handleModelChange(opt?.value ?? '')} isDisabled={!form.manufacturerId} isLoading={isLoadingModels} menuPortalTarget={typeof document !== 'undefined' ? document.body : null} /></div>
                                        <div><label className={LABEL_CLASS}>Criticality <span className="text-gray-400 font-normal">(optional)</span></label><Select options={CRITICALITY_OPTIONS} placeholder="Select criticality" value={form.criticality || ''} onChange={(opt) => handleChange('criticality', opt?.value ?? '')} menuPortalTarget={typeof document !== 'undefined' ? document.body : null} /></div>
                                    </div>
                                </div>
                                <div className={CARD_SECTION}><label className={LABEL_CLASS}>Notes <span className="text-gray-400 font-normal">(optional)</span></label><textarea value={form.notes} onChange={(e) => handleChange('notes', e.target.value ?? '')} rows={3} placeholder="Notes…" className={`${INPUT_BASE} resize-none`} /></div>
                            </div>
                        </motion.div>
                    )}
                    {activeTabIndex === 1 && (
                        <motion.div key="location" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }} className={TAB_PANEL}>
                            <div className="space-y-5">
                                <div className={CARD_SECTION}>
                                    <h4 className={SECTION_TITLE}><MapPin size={18} /> Zone & Block</h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                        <div><label className={LABEL_CLASS}>Zone</label><input type="text" value={form.zone} onChange={(e) => handleChange('zone', e.target.value ?? '')} placeholder="Zone" className={INPUT_BASE} /></div>
                                        <div><label className={LABEL_CLASS}>Block</label><input type="text" value={form.block} onChange={(e) => handleChange('block', e.target.value ?? '')} placeholder="Block" className={INPUT_BASE} /></div>
                                        <div className="sm:col-span-2"><label className={LABEL_CLASS}>Sub-Location</label><input type="text" value={form.subLocation} onChange={(e) => handleChange('subLocation', e.target.value ?? '')} placeholder="Sub-location" className={INPUT_BASE} /></div>
                                    </div>
                                </div>
                                <div className={CARD_SECTION}>
                                    <h4 className={SECTION_TITLE}>Installation</h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                        <div><label className={LABEL_CLASS}>Area</label><input type="text" value={form.installationLocation.area} onChange={(e) => handleNestedChange('installationLocation', 'area', e.target.value ?? '')} placeholder="e.g. Main Hall, Section A" className={INPUT_BASE} /></div>
                                        <div><label className={LABEL_CLASS}>Rack</label><input type="text" value={form.installationLocation.rack} onChange={(e) => handleNestedChange('installationLocation', 'rack', e.target.value ?? '')} placeholder="e.g. Rack 01" className={INPUT_BASE} /></div>
                                        <div><label className={LABEL_CLASS}>Position</label><input type="text" value={form.installationLocation.position} onChange={(e) => handleNestedChange('installationLocation', 'position', e.target.value ?? '')} placeholder="e.g. Slot 3, Bay 2" className={INPUT_BASE} /></div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                    {activeTabIndex === 2 && (
                        <motion.div key="specs" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }} className={TAB_PANEL}>
                            <div className="space-y-5">
                                <div className={CARD_SECTION}>
                                    <h4 className={SECTION_TITLE}><Zap size={18} /> Electrical</h4>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                        {(['voltage', 'current', 'power', 'frequency', 'phase', 'class'] as const).map((key) => {
                                            const placeholders: Record<string, string> = { voltage: 'e.g. 230V', current: 'e.g. 10A', power: 'e.g. 2.3 kW', frequency: 'e.g. 50 Hz', phase: 'e.g. 3-Phase', class: 'e.g. Class I' };
                                            const labels: Record<string, string> = { voltage: 'Voltage', current: 'Current', power: 'Power', frequency: 'Frequency', phase: 'Phase', class: 'Class' };
                                            return (
                                            <div key={key}><label className={LABEL_CLASS}>{labels[key]}</label><input type="text" value={form.electricalRatings[key]} onChange={(e) => handleNestedChange('electricalRatings', key, e.target.value ?? '')} placeholder={placeholders[key]} className={INPUT_BASE} /></div>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className={CARD_SECTION}><label className={LABEL_CLASS}>Technical specs</label><textarea value={form.technicalSpecifications} onChange={(e) => handleChange('technicalSpecifications', e.target.value ?? '')} rows={4} placeholder="Specifications…" className={`${INPUT_BASE} font-mono text-xs resize-none`} /></div>
                            </div>
                        </motion.div>
                    )}
                    {activeTabIndex === 3 && (
                        <motion.div key="lifecycle" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }} className={TAB_PANEL}>
                            <div className="space-y-5">
                                <div className={CARD_SECTION}>
                                    <h4 className={SECTION_TITLE}>Dates</h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div><label className={LABEL_CLASS}>Install date</label><input type="date" value={form.installationDate} onChange={(e) => handleChange('installationDate', e.target.value)} className={INPUT_BASE} /></div>
                                        <div><label className={LABEL_CLASS}>Commission date</label><input type="date" value={form.commissioningDate} onChange={(e) => handleChange('commissioningDate', e.target.value)} className={INPUT_BASE} /></div>
                                    </div>
                                </div>
                                <div className={CARD_SECTION}>
                                    <h4 className={SECTION_TITLE}>Vendor</h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        <div className="sm:col-span-2 lg:col-span-3"><label className={LABEL_CLASS}>Vendor Name</label><input type="text" value={form.vendor.name} onChange={(e) => handleNestedChange('vendor', 'name', e.target.value ?? '')} placeholder="e.g. Acme Electrical Pvt Ltd" className={INPUT_BASE} /></div>
                                        <div><label className={LABEL_CLASS}>Contact Person</label><input type="text" value={form.vendor.contactPerson} onChange={handleContactPersonInput} onKeyDown={handleContactPersonKeyDown} placeholder="e.g. John Doe" className={INPUT_BASE} /></div>
                                        <div><label className={LABEL_CLASS}>Phone</label><input type="tel" value={form.vendor.phone} onChange={handlePhoneInput} onKeyDown={handlePhoneKeyDown} placeholder="e.g. +91 98765 43210" className={INPUT_BASE} /></div>
                                        <div>
                                            <label className={LABEL_CLASS}>Email</label>
                                            <input type="email" value={form.vendor.email} onChange={handleEmailInput} placeholder="e.g. vendor@example.com" className={`${INPUT_BASE} ${emailError ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20' : ''}`} />
                                            {emailError && <p className="mt-1 text-xs text-red-500">{emailError}</p>}
                                        </div>
                                        <div className="sm:col-span-2"><label className={LABEL_CLASS}>Address</label><input type="text" value={form.vendor.address} onChange={(e) => handleNestedChange('vendor', 'address', e.target.value ?? '')} placeholder="e.g. 123 Industrial Area, Mumbai" className={INPUT_BASE} /></div>
                                    </div>
                                </div>
                                <div className={CARD_SECTION}>
                                    <div className="flex items-center justify-between mb-2">
                                        <h4 className={SECTION_TITLE}>Warranty</h4>
                                        <Button variant="ghost" size="sm" disabled={uploadingWarrantyDoc} startIcon={uploadingWarrantyDoc ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} onClick={() => handleSectionDocUpload('Warranty')}>
                                            {uploadingWarrantyDoc ? 'Uploading…' : 'Upload doc'}
                                        </Button>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                        <div><label className={LABEL_CLASS}>Start</label><input type="date" value={form.warranty.startDate} onChange={(e) => handleNestedChange('warranty', 'startDate', e.target.value)} className={INPUT_BASE} /></div>
                                        <div><label className={LABEL_CLASS}>End</label><input type="date" value={form.warranty.endDate} onChange={(e) => handleNestedChange('warranty', 'endDate', e.target.value)} className={INPUT_BASE} /></div>
                                        <div><label className={LABEL_CLASS}>Provider</label><input type="text" value={form.warranty.provider} onChange={(e) => handleNestedChange('warranty', 'provider', e.target.value ?? '')} placeholder="e.g. OEM Warranty Corp" className={INPUT_BASE} /></div>
                                        <div><label className={LABEL_CLASS}>Years</label><InputNumber value={form.warranty.termsYears} onValueChange={(e) => handleNestedChange('warranty', 'termsYears', e.value ?? 0)} min={0} placeholder="e.g. 3" className="w-full [&_.p-inputnumber]:w-full [&_.p-inputnumber-input]:w-full [&_.p-inputnumber-input]:rounded-xl [&_.p-inputnumber-input]:h-11 [&_.p-inputnumber-input]:border-gray-300" /></div>
                                        <div className="sm:col-span-2 lg:col-span-4"><label className={LABEL_CLASS}>Terms</label><textarea value={form.warranty.terms} onChange={(e) => handleNestedChange('warranty', 'terms', e.target.value ?? '')} rows={1} placeholder="e.g. Covers parts and labour for manufacturing defects" className={`${INPUT_BASE} resize-none`} /></div>
                                    </div>
                                    {/* Warranty Docs */}
                                    {documents.filter(d => d.documentType === 'Warranty' || d.documentType === 'Warranty Certificates').length > 0 && (
                                        <div className="mt-3 space-y-2">
                                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Warranty Documents</p>
                                            {documents.filter(d => d.documentType === 'Warranty' || d.documentType === 'Warranty Certificates').map((doc, idx) => (
                                                <div key={doc._id ?? idx} className="flex items-center justify-between p-2.5 rounded-lg border border-gray-200 bg-white hover:border-[#339274]/30 transition-all">
                                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                                        {looksLikeImageMediaUrl(doc.documentFile) ? (
                                                            <ResolvedImage mediaSrc={doc.documentFile} alt="" className="h-7 w-7 shrink-0 rounded-md object-cover ring-1 ring-amber-200" />
                                                        ) : (
                                                            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-amber-50"><FileText className="h-3.5 w-3.5 text-amber-600" /></div>
                                                        )}
                                                        <span className="text-sm text-gray-800 truncate">{doc.documentName}</span>
                                                    </div>
                                                    <div className="flex gap-0.5 shrink-0">
                                                        <button type="button" onClick={() => void fileService.openMediaInNewTab(doc.documentFile)} className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 hover:text-[#339274] transition-colors" title="Open / download"><Download size={15} /></button>
                                                        <button type="button" onClick={() => handleDeleteDocument(doc._id)} className="p-1.5 rounded-md text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors" title="Delete"><Trash2 size={15} /></button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className={CARD_SECTION}>
                                    <div className="flex items-center justify-between mb-2">
                                        <h4 className={SECTION_TITLE}>Insurance</h4>
                                        <Button variant="ghost" size="sm" disabled={uploadingInsuranceDoc} startIcon={uploadingInsuranceDoc ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} onClick={() => handleSectionDocUpload('Insurance')}>
                                            {uploadingInsuranceDoc ? 'Uploading…' : 'Upload doc'}
                                        </Button>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                        <div><label className={LABEL_CLASS}>Provider</label><input type="text" value={form.insurance.provider} onChange={(e) => handleNestedChange('insurance', 'provider', e.target.value ?? '')} placeholder="e.g. National Insurance Co" className={INPUT_BASE} /></div>
                                        <div><label className={LABEL_CLASS}>Policy No.</label><input type="text" value={form.insurance.policyNumber} onChange={(e) => handleNestedChange('insurance', 'policyNumber', e.target.value ?? '')} placeholder="e.g. INS-2024-00123" className={INPUT_BASE} /></div>
                                        <div><label className={LABEL_CLASS}>Start</label><input type="date" value={form.insurance.startDate} onChange={(e) => handleNestedChange('insurance', 'startDate', e.target.value)} className={INPUT_BASE} /></div>
                                        <div><label className={LABEL_CLASS}>End</label><input type="date" value={form.insurance.endDate} onChange={(e) => handleNestedChange('insurance', 'endDate', e.target.value)} className={INPUT_BASE} /></div>
                                        <div className="sm:col-span-2 lg:col-span-4"><label className={LABEL_CLASS}>Coverage</label><textarea value={form.insurance.coverageDetails} onChange={(e) => handleNestedChange('insurance', 'coverageDetails', e.target.value ?? '')} rows={1} placeholder="e.g. Comprehensive coverage including fire, flood, and accidental damage" className={`${INPUT_BASE} resize-none`} /></div>
                                    </div>
                                    {/* Insurance Docs */}
                                    {documents.filter(d => d.documentType === 'Insurance' || d.documentType === 'Insurance Certificate').length > 0 && (
                                        <div className="mt-3 space-y-2">
                                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Insurance Documents</p>
                                            {documents.filter(d => d.documentType === 'Insurance' || d.documentType === 'Insurance Certificate').map((doc, idx) => (
                                                <div key={doc._id ?? idx} className="flex items-center justify-between p-2.5 rounded-lg border border-gray-200 bg-white hover:border-[#339274]/30 transition-all">
                                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                                        {looksLikeImageMediaUrl(doc.documentFile) ? (
                                                            <ResolvedImage mediaSrc={doc.documentFile} alt="" className="h-7 w-7 shrink-0 rounded-md object-cover ring-1 ring-blue-200" />
                                                        ) : (
                                                            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-blue-50"><FileText className="h-3.5 w-3.5 text-blue-600" /></div>
                                                        )}
                                                        <span className="text-sm text-gray-800 truncate">{doc.documentName}</span>
                                                    </div>
                                                    <div className="flex gap-0.5 shrink-0">
                                                        <button type="button" onClick={() => void fileService.openMediaInNewTab(doc.documentFile)} className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 hover:text-[#339274] transition-colors" title="Open / download"><Download size={15} /></button>
                                                        <button type="button" onClick={() => handleDeleteDocument(doc._id)} className="p-1.5 rounded-md text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors" title="Delete"><Trash2 size={15} /></button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    )}
                    {activeTabIndex === 4 && (
                        <motion.div key="documents" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }} className={TAB_PANEL}>
                            <div className="space-y-5">
                                <div className={CARD_SECTION}>
                                    <h4 className={SECTION_TITLE}><Upload size={18} /> Upload</h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                        <div className="sm:col-span-2"><label className={LABEL_CLASS}>Name</label><input type="text" value={newDocument.documentName} onChange={(e) => setNewDocument(prev => ({ ...prev, documentName: e.target.value ?? '' }))} placeholder="Document name" className={INPUT_BASE} /></div>
                                        <div><label className={LABEL_CLASS}>Type</label><Select options={documentTypeOptions} placeholder="Select document type" value={newDocument.documentType} onChange={(opt) => setNewDocument(prev => ({ ...prev, documentType: opt?.value ?? 'Manual' }))} menuPortalTarget={typeof document !== 'undefined' ? document.body : null} /></div>
                                        <div><label className={LABEL_CLASS}>File key / URL</label><input type="text" value={newDocument.documentFile} onChange={(e) => setNewDocument(prev => ({ ...prev, documentFile: e.target.value ?? '' }))} placeholder="S3 key or https URL" className={INPUT_BASE} /></div>
                                        <div className="sm:col-span-4 flex items-end">
                                            <Button size="sm" startIcon={uploadingDoc ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} disabled={uploadingDoc || !newDocument.documentName || !newDocument.documentFile} onClick={handleUploadDocument}>{uploadingDoc ? 'Uploading…' : 'Upload'}</Button>
                                        </div>
                                    </div>
                                </div>
                                {documents.length > 0 ? (
                                    <div className="space-y-2">
                                        {documents.map((doc, index) => (
                                            <div key={doc._id ?? index} className="flex items-center justify-between p-3 rounded-xl border border-gray-200 bg-white hover:border-[#339274]/30 hover:shadow-sm transition-all">
                                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                                    {looksLikeImageMediaUrl(doc.documentFile) ? (
                                                        <ResolvedImage mediaSrc={doc.documentFile} alt="" className="h-9 w-9 shrink-0 rounded-lg object-cover ring-1 ring-gray-200" />
                                                    ) : (
                                                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#339274]/10"><FileText className="h-4 w-4 text-[#339274]" /></div>
                                                    )}
                                                    <div className="min-w-0"><p className="text-sm font-medium text-gray-900 truncate">{doc.documentName}</p><p className="text-xs text-gray-500">{doc.documentType}</p></div>
                                                </div>
                                                <div className="flex gap-1 shrink-0">
                                                    <button type="button" onClick={() => void fileService.openMediaInNewTab(doc.documentFile)} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-[#339274] transition-colors" title="Open / download"><Download size={18} /></button>
                                                    <button type="button" onClick={() => handleDeleteDocument(doc._id)} className="p-2 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors" title="Delete"><Trash2 size={18} /></button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="py-10 text-center rounded-xl border-2 border-dashed border-gray-200 bg-gray-50">
                                        <FileText className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                                        <p className="text-sm text-gray-500">No documents attached</p>
                                        <p className="text-xs text-gray-400 mt-1">Use the form above to add one</p>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
                    </>
                )}
            </div>
        </RightDrawer>
    );
};

export default EquipmentEditDrawer;
